import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { cloneDeep, debounce, update } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { getUpdatedData } from "./NewRecord";
import { eachDeep } from "deepdash-es/standalone";
import { RECORD_TYPE, FIELD_TYPE, OTHER_TYPE } from "../../../constants/datatypes";
import { t } from "i18next";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genKey, nestedNodeIdAssign } from "../../../utils/utilFunctions";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import { element } from "prop-types";
import { addNodesAndUpdateTransData, isElseErrorValidator, nodesValidation, removeNodesAndUpdateTransData } from "../../../utils/nodesValidation";

const initialMiddleProcessObject = {
  status: false,
  index: null,
  isInside: false,
  pasteModal: false,
};

const CopyAndPaste = ({ isCopied, setIsCopied, contextMenuType, treeType }) => {
  const { t } = useTranslation();
  const [copiedNodes, setCopiedNodes] = useState([]);
  const [inMiddleProcess, setInMiddleProcess] = useState(
    initialMiddleProcessObject
  );
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState("");
  const [pasteModal, setPasteModal] = useState(false);
  const parentScreen = useContext(ParentScreenCtx);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState('');
  const { rightTree, leftTree } = useSelector((state) => state.treeData[parentScreen]);
  const currentTab = rightTree.currentTab;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const { selectedKeysArray, translatedData } = rightTree.tree[currentTab];
  const [copiedNodesPending, setCopiedNodesPending] = useState() // This state will be used for storing nodes until user select any option in modal;
  const debounceRef = useRef(Math.random(0));
  const isInMiddleProcess = useRef(inMiddleProcess);
  const leftTreeSelectedNode = leftTree.tree[currentTab].selectedNode

  const contentRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    if(isLoading) return;
    if (isCopied?.copy && !isCopied?.paste) {
      let copiedNodes = getSelectedNodes(selectedKeysArray, translatedData, true) || [];
      const isElseError = isElseErrorValidator(copiedNodes);
      if(isElseError) {
        setError(t("BOX_CANNOT_COPY_ITEM_COPY_IF_NOT_ELSE"));
        setShowError(true);
        setIsCopied({ ...isCopied, copy: false, copiedNodes: []});
      }
      else {
        copiedNodes = copiedNodes?.filter(ele => ele?.ICON_DISPLAY !== 'TEMPLATE_BODY_DOTS');
        let copyErrorHandlers = [];
        if (parentScreen === "workflow") {
          const errorHandlers= rightTree.tree.templateHeader?.WORKFLOW?.ERROR_HANDLER_LIST
          copyErrorHandlers  = getErrorHandlersForNodes(copiedNodes, errorHandlers);
        }
        setIsCopied({ ...isCopied, 
          copy: false, 
          copiedNodes: copiedNodes, 
          copiedErrorHandler:copyErrorHandlers,
          copiedSourceTemplate:leftTreeSelectedNode?.title || "",
          copiedSourceWorkflow:leftTreeSelectedNode?.workflowGroup || ""
        });
      }

    } else if (isCopied?.paste && (contextMenuType === isCopied?.dispatchFrom) && treeType === isCopied?.treeType && isCopied?.copiedNodes?.length && !rightTree.loading) {
      // const {updatedData, removedNodes} = removeNodesAndUpdateTransData(translatedData, isCopied.copiedNodes)
      let copiedNodesTemp =  isCopied.copiedNodes;
      let tempraryErrorHandlers = [];

      
      setIsLoading(true);
      const {errorMessage, modalMessage, isInside} = nodesValidation(selectedNode, isCopied.copiedNodes, currentTab, false, templateHeader?.FILETYPE, translatedData, parentScreen);
      if (modalMessage){
        handleDialogShow(t(modalMessage));
        if (isCopied?.copiedErrorHandler?.length && isCopied?.copiedSourceTemplate !== leftTreeSelectedNode?.title  && parentScreen === "workflow") {
          const {updatedHandlers, copiedNodes} = getUpdatedErrorHandlers(rightTree.tree.templateHeader?.WORKFLOW?.ERROR_HANDLER_LIST, isCopied?.copiedErrorHandler, copiedNodesTemp);
          copiedNodesTemp = copiedNodes;
          tempraryErrorHandlers = updatedHandlers;
        }
		    const selectedNodes = getSelectedNodes([selectedNode.key], translatedData, true); // If pasted on IF node so it should also consider else node
        setCopiedNodesPending({ updatedData: translatedData, removedNodes: copiedNodesTemp, node: selectedNodes?.length === 2 ? selectedNodes : selectedNode?.[0] || selectedNode, updatedHandlers: tempraryErrorHandlers})
      }
      else if (errorMessage){
        setError(t(errorMessage));
        setShowError(true);
        setIsLoading(false);
      }
      else if(!pasteModal && !errorMessage) {
        if (isCopied?.copiedErrorHandler?.length && isCopied?.copiedSourceTemplate !== leftTreeSelectedNode?.title && parentScreen === "workflow"){
          const {updatedHandlers, copiedNodes} = getUpdatedErrorHandlers(rightTree.tree.templateHeader?.WORKFLOW?.ERROR_HANDLER_LIST, isCopied?.copiedErrorHandler, copiedNodesTemp);
          copiedNodesTemp = copiedNodes;
          dispatch(treeActions.setWorkflowProperties({
            ...rightTree.tree.templateHeader,
            WORKFLOW: {
              ...rightTree.tree.templateHeader.WORKFLOW,
              "ERROR_HANDLER_LIST": updatedHandlers
            }
          }));
        }
        const isCopiedFromDifferentSource = isCopied?.copiedSourceWorkflow !== leftTreeSelectedNode.workflowGroup ? true : isCopied?.copiedSourceTemplate !== leftTreeSelectedNode.title ? true : false;
        const {translatedData: newTransData , selectedNode: finalNode, newSelectedKeys} = addNodesAndUpdateTransData(translatedData, copiedNodesTemp, selectedNode, isInside, templateHeader.FILETYPE, currentTab, parentScreen, false, isCopiedFromDifferentSource) || {};
        if (!newTransData && !finalNode && !newSelectedKeys) return;
        updateNewData(newTransData, finalNode, newSelectedKeys);
      }
      setIsCopied({...isCopied, paste: false});
    }
  }, [isCopied]);

    useEffect(() => {
      document.body.classList.toggle("loading-cursor", isLoading);
    }, [isLoading]);

  const handlePaste = (isInside) => {
    let {updatedData, removedNodes, node, updatedHandlers, } = copiedNodesPending;
    if (Array.isArray(node)) {
      node = isInside ? node[0] : node?.[1] || node?.[0];
    }
    if (updatedHandlers?.length) {
      dispatch(treeActions.setWorkflowProperties({
        ...rightTree.tree.templateHeader,
        WORKFLOW: {
          ...rightTree.tree.templateHeader.WORKFLOW,
          "ERROR_HANDLER_LIST": updatedHandlers
        }
      }));
    }
    const isCopiedFromDifferentSource = isCopied?.copiedSourceWorkflow !== leftTreeSelectedNode.workflowGroup ? true : isCopied?.copiedSourceTemplate !== leftTreeSelectedNode.title ? true : false;
    const {translatedData: newTransData , selectedNode: finalNode, newSelectedKeys} = addNodesAndUpdateTransData(translatedData, removedNodes, node, isInside, templateHeader.FILETYPE, currentTab, parentScreen, false, isCopiedFromDifferentSource) || {};
    updateNewData(newTransData, finalNode, newSelectedKeys);
    setPasteModal(false);
  };

  const formatModalContent = (string) => {
    let splitText = string.split(`\\n`);
  
    return (
      <>
        {splitText[0]}
        <br/><br/>
        {splitText[2]} 
        <br/>
        {splitText[3]} <br/>
      </>
    )
  };  

  const handleDialogShow = (modalText) => {
    setContent(formatModalContent(modalText) );
    setPasteModal(true);
  }

  const updateNewData = (finalData, finalNode, newSelectedKeys) => {
    const newUpdatedData = getUpdatedData(finalData, templateHeader?.FILETYPE, parentScreen, currentTab);
    
    dispatch(treeActions.setRightTreeSelectedNode({
      screen : parentScreen,
      type: currentTab,
      data: {}
    }))
    setTimeout(()=>{                   
      dispatch(treeActions.setRightTreeSelectedNode({
        screen : parentScreen,
        type: currentTab,
        data: finalNode
      }))
    })
    dispatch(treeActions.setRightTreeTransData({
      screen: parentScreen,
      type: currentTab,
      data: newUpdatedData
    }))
    setTimeout(() => {
      setIsLoading(false);
    })
  }



  return (<>
    <Dialog open={pasteModal && isCopied?.treeType === parentScreen && contextMenuType === isCopied?.dispatchFrom} maxWidth="xs" fullWidth>
      <DialogTitle className="dialog-header">
        <div className="title">{t("BOX_CONFIRM")}</div>
      </DialogTitle>
      <DialogContent className="dialog-content" dividers>
        <div ref={contentRef}>{content}</div>
      </DialogContent>
      <DialogActions>
        <Button
          size='small'
          variant="outlined"
          color="primary"
          onClick={() => {
            handlePaste(true);
          }}
        >
          {t("BTN_YES")}
        </Button>
        <Button
          size='small'
          variant="outlined"
          color="primary"
          onClick={() => {
            handlePaste(false)
          }}
        >
          {t("BTN_NO")}
        </Button>
        <Button
          size='small'
          variant="outlined"
          color="primary"
          onClick={() => {
            setPasteModal(false);    
            setTimeout(() => {
              setIsLoading(false);
            })      
          }}
        >
          {t("BTN_CANCEL")}
        </Button>
      </DialogActions>
    </Dialog>
    
      {contextMenuType === isCopied?.dispatchFrom && <ErrorModal calledFrom={"Copy Paste"} errorModal={showError} errorMessage={error} handleErrorModal={()=>{
        setShowError(false);
      }} />}
  </>
  );
};

export default CopyAndPaste;

export const getSelectedNodes = (keysArray, translatedData, copyElse) => {
  const data = cloneDeep(translatedData);

  ///--------- Checking if Parent Node  or Child is Selected from Keys.------------///
  let newKeysArray = keysArray.map((key, index) => {
    return { index, key };
  });

  newKeysArray.sort((a, b) => {
    return a.key?.length - b.key?.length;
  });

  newKeysArray?.forEach((ele, idx) => {
    newKeysArray?.forEach((ele1, idx1) => {
      const key1 = ele?.key?.split("-");
      const key2 = ele1?.key?.split("-");
      const matchedKey = key1?.filter((ele, idx) => ele === key2[idx]);

      if (
        idx !== idx1 &&
        matchedKey?.length === key1?.length &&
        key1?.length < key2?.length
      ) {
        ele1["isChildNode"] = true;
      }
    });
  });

  //Selecting Valid Node to Be copied.
  newKeysArray = newKeysArray?.filter((ele) => {
    return !ele?.isChildNode;
  });

  ///-------------- Getting Nodes From Keys (Sorted in First Field Type Nodes Then Record Type)------------///

  let copiedNodesArray = [];

  newKeysArray?.forEach((ele) => {
    getNodeByKey(ele?.key);
  });

  // Optimized way of finding node.
  function getNodeByKey(key, isElseCopy) {
    eachDeep(data, (obj, i) => {
      if (!isElseCopy && obj?.key === key && obj?.ICON_DISPLAY === "CONDITION_IF") {
        // Only include ELSE node if copyElse is true
        if (copyElse) {
          let newEleKey = obj?.key?.split('-');
          newEleKey[newEleKey?.length - 1] = +newEleKey[newEleKey?.length - 1] + +1
          newEleKey = newEleKey?.join('-');
          getNodeByKey(newEleKey, true);
        }
        copiedNodesArray?.push(obj)
        return;
      }

      if (isElseCopy && key === obj?.key && obj?.ICON_DISPLAY === "CONDITION_ELSE") {
        copiedNodesArray?.push(obj)
        return;
      }
      else if (obj?.key === key && !isElseCopy) {
        if (!copiedNodesArray?.filter(ele => ele?.key === obj?.key)?.length) copiedNodesArray?.push(obj)
        return;
      }
    })
  }

  const getNodeInOrder = (data) => {

    // Bubble Sort to sort Record in hierarchical Order
    for (let i = data.length - 1; i >= 0; i--) {
      for (let j = 0; j < i; j++) {

        let key1 = data[j].key;
        let key2 = data[j + 1].key
        const keysObj = getStartAndEndKey(key1, key2);

        //Swaping Records
        if (keysObj.startKey === key2) {
          const tempObj = data[j];
          data[j] = data[j + 1];
          data[j + 1] = tempObj;
        }

      }
    }

  }

  getNodeInOrder(copiedNodesArray);
  return copiedNodesArray;
};

export const getTitleOfCopiedNode = (parentArray, { title, ICON_DISPLAY, TYPE, ELEMENT_NAME, ATTRIBUTE_NAME }) => {
  ////--------------------Function to Get New Record New On Every Record Creation------------------------////
  // console.log(parentArray, title);
  const copiedTitle = ICON_DISPLAY?.includes("ELEMENT") ? (ELEMENT_NAME || TYPE || title)?.replace(/^<|>$/g, "") : FIELD_TYPE[ICON_DISPLAY]
  ? title.replace(`${FIELD_TYPE[ICON_DISPLAY].type}: `, "") : title;
  const data = cloneDeep(parentArray || []);
  const copiedNodeTitle = FIELD_TYPE?.[ICON_DISPLAY]
    ? title.replace(`${FIELD_TYPE[ICON_DISPLAY].type}: `, "")
    : ICON_DISPLAY?.includes("ELEMENT") ? (ELEMENT_NAME || TYPE || title)?.replace(/^<|>$/g, "") : title;
  let titleNumber;
  const getTitleName = (data) => {
    data?.forEach((ele, idx) => {
      let title = FIELD_TYPE[ele.ICON_DISPLAY]
        ? ele.title.replace(`${FIELD_TYPE[ele.ICON_DISPLAY]?.type}: `, "")
        : ele?.ICON_DISPLAY.includes("ELEMENT") ? (ele?.ELEMENT_NAME || ele?.TYPE || ele?.title)?.replace(/^<|>$/g, "") : ele?.title;
      const firstOccurance = title?.indexOf(t("FRG_COPY_OF_"));
      const startingBracket = title?.indexOf("(");
      const endingBracket = title?.indexOf(")");
      let previousNumberOfCopy;
      if (titleNumber === undefined && copiedTitle === title) {
        titleNumber = true;
      }
      // console.log(title, copiedTitle, titleNumber, ele);
      if (
        startingBracket >= 0 &&
        endingBracket >= 0 &&
        endingBracket > startingBracket &&
        endingBracket - startingBracket + t("FRG_COPY_OF_")?.length >= 1 &&
        startingBracket - (firstOccurance + t("FRG_COPY_OF_")?.length - 1) === 1
      ) {
        previousNumberOfCopy = title?.substring(
          startingBracket + 1,
          endingBracket
        );
        title =
          title?.substring(0, startingBracket) + title?.slice(endingBracket + 1);
      } else {
        previousNumberOfCopy = false;
      }
      if (title === `${t("FRG_COPY_OF_")} ${copiedNodeTitle}`) {
        // console.log("Reached", `${t("FRG_COPY_OF_")} ${copiedNodeTitle}`)
        if (titleNumber === undefined || titleNumber === true) titleNumber = 0;
        if (
          startingBracket >= 0 &&
          endingBracket >= 0 &&
          !isNaN(previousNumberOfCopy)
        ) {
          titleNumber =
            Number(previousNumberOfCopy) > titleNumber
              ? Number(previousNumberOfCopy)
              : titleNumber;
        }
      }
    });

  };
  getTitleName(data, copiedTitle);


  return titleNumber === undefined
    ? `${copiedNodeTitle}` : titleNumber === true ? `${t("FRG_COPY_OF_")} ${copiedNodeTitle}`
    : `${t("FRG_COPY_OF_")}(${titleNumber + 1}) ${copiedNodeTitle}`;
};


export const getRecordInsertingPosition = (data, node, isInside, screen, currentTab) => {
  const startingKey = { inbound: 'ib', outbound: {header: "obh-0", body: "ob-0", trailer: "obt-0"}[currentTab], workflow: 'wf' }[screen];
  let index;
  let key;
  if (screen === 'workflow') {
    if (isInside) {
      // Add a child to the selected node
      const parentKey = node?.key || startingKey; 
      const childIndex = isInside ? 0 : data.length; 
      key = `${parentKey}-${childIndex}`; 
      index = childIndex; 
    } else {
      const parentKeyParts = node?.key?.split('-') || [startingKey];
      const siblingIndex = Number(parentKeyParts.pop()) + 1; 
      parentKeyParts.push(siblingIndex); 
      key = parentKeyParts.join('-'); 
      index = siblingIndex; 
    }
  }
  else if ((RECORD_TYPE[node?.ICON_DISPLAY] ) && !isInside) {
    let newKey = node?.key?.split('-');
    index = Number(newKey?.splice(newKey.length - 1, 1)) + 1;
    newKey?.push(index);
    key = newKey?.join('-');
  }
  else {
    let found;
    for (let i = 0; i < data.length; i++) {
      const ele = data[i];
      if (((screen === 'inbound' && RECORD_TYPE[ele?.ICON_DISPLAY] ) || ele.ICON_DISPLAY.includes('RECORD'))) {
        let newKey = ele?.key?.split('-');
        index = Number(newKey?.splice(newKey?.length - 1, 1));
        newKey?.push(index);
        key = newKey?.join('-');
        found = true;
        break;
      }
    }

    if (!found) {
      let newKey = node?.key?.split('-') || [startingKey];
      if (!isInside) {
        newKey?.splice(newKey.length - 1);
      }
      newKey?.push(data.length);
      newKey = newKey.join('-');
      key = isInside ? `${node?.key}-${data?.length}` : newKey;
      index = data?.length;
    }

  }

  return { index, key };

}

// Function to get Starting selection point and Ending selection point.
export function getStartAndEndKey(key1, key2) {

  const key1Array = key1?.split("-")?.splice(1);
  const key2Array = key2?.split("-")?.splice(1);
  const size1 = key1Array?.length;
  const size2 = key2Array?.length;

  let key1Smaller = false;

  if (size1 < size2) {

    for (let i = 0; i < size1; i++) {
      const num1 = Number(key1Array[i]);
      const num2 = Number(key2Array[i]);
      if (num1 > num2) { key1Smaller = false; break; }
      if (num1 < num2) { key1Smaller = true; break; }
    }
    if (key2.includes(key1)) key1Smaller = true;

  } else if (size2 < size1) {

    for (let i = 0; i < size2; i++) {
      const num1 = Number(key1Array[i]);
      const num2 = Number(key2Array[i]);
      if (num1 > num2) { key1Smaller = false; break; }
      if (num1 < num2) { key1Smaller = true; break; }
    }

  } else {

    for (let i = 0; i < size1; i++) {
      const num1 = Number(key1Array[i]);
      const num2 = Number(key2Array[i]);
      if (num1 > num2) { key1Smaller = false; break; }
      if (num1 < num2) { key1Smaller = true; break; }
    }

  }

  return key1Smaller
    ? { startKey: key1, endKey: key2 }
    : { startKey: key2, endKey: key1 };
}


const getErrorHandlersForNodes = (nodes = [], errorHandlers = []) =>  {
  const copyErrorHandlers = [];
  const loopNodes = (nodes = []) => {
    nodes?.forEach(node =>{
      if (node?.ICON_DISPLAY === "ERROR_HANDLER") {
        const errorHandler =  errorHandlers?.find(o => o?.ID === node?.OUTBOUND_ID)
        if (errorHandler && !copyErrorHandlers?.find(o => o?.ID === node?.OUTBOUND_ID)) {
          copyErrorHandlers?.push(errorHandler);
        }
      }
      if (node?.children) {
        loopNodes(node?.children);
      }
    })
  }
  loopNodes(nodes);
  return copyErrorHandlers;
}

const getUpdatedErrorHandlers  =  (previousErrorHandlers = [], newErrorHandlers = [], copiedNodes = []) => {
  let newHandlers = [];
  let colIdArr = [];
  previousErrorHandlers.forEach((node) => colIdArr.push(node?.ID));
  let newID = colIdArr.length > 0 ? Math.max(...colIdArr) + 2 : 1;
  newHandlers = [...previousErrorHandlers];
  const clonedData = cloneDeep(copiedNodes);


  const findAndUpdateErrorHandler = (id, newID, data) => {
    data?.forEach((node) => {
      if (node?.ICON_DISPLAY === "ERROR_HANDLER" && node?.OUTBOUND_ID === id) {
        node.OUTBOUND_ID = newID;
      }
      if (node.children) {
        findAndUpdateErrorHandler(id, newID, node.children);
      }
    })
  }


  newErrorHandlers?.forEach(e => {
    const foundHandlerByID = newHandlers.find(o => e?.ID === o?.ID);
    const foundHandlerByName = newHandlers.find(o => e?.OUTBOUND_NAME === o?.OUTBOUND_NAME);
    if (!foundHandlerByID && !foundHandlerByName) {
      newHandlers?.push(e);
    }else if (foundHandlerByID && !foundHandlerByName) {
      newID  =  newID + 2;
      findAndUpdateErrorHandler(e?.ID, newID,  clonedData )
      newHandlers.push({...e, ID: newID, ...(e?.OUTBOUND ? {...e?.OUTBOUND, ID: newID} : {})});
    }else {
      findAndUpdateErrorHandler(e?.ID, foundHandlerByName?.ID,  clonedData )
    }
  })


  

  
  return {updatedHandlers :  newHandlers, copiedNodes: clonedData};
}